'use client'

import Link from 'next/link'
import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CategoryBanner } from '@/src/utils/data'
import ImageComponent from '../ImageComponent'
import { ArrowUpRightSVG } from '@/src/ui/AllSVG'
import { getCategoriesPath } from '@/src/routes/routes'

gsap.registerPlugin(ScrollTrigger)

function CategoryCard({ item }: { item: CategoryBanner }) {
  const cardRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)

  useGSAP(() => {
    const card = cardRef.current
    const text = textRef.current
    if (!card || !text) return

    const maxY = () => Math.max(0, card.offsetHeight - text.offsetHeight)
    const mm = gsap.matchMedia()

    gsap.set(text, { y: 0, force3D: true })

    mm.add('(min-width: 768px)', () => {
      gsap.to(text, {
        y: maxY,
        ease: 'none',
        scrollTrigger: {
          trigger: card,
          start: 'top 50%',
          end: 'bottom 50%',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      })
    })

    mm.add('(max-width: 767px)', () => {
      gsap.to(text, {
        y: maxY,
        ease: 'none',
        scrollTrigger: {
          trigger: card,
          start: 'top 92%',
          end: 'bottom 50%',
          scrub: 1,
          invalidateOnRefresh: true,
        },
      })
    })

    let refreshRaf = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(refreshRaf)
      refreshRaf = requestAnimationFrame(() => ScrollTrigger.refresh())
    })
    observer.observe(card)

    return () => {
      mm.revert()
      cancelAnimationFrame(refreshRaf)
      observer.disconnect()
    }
  }, { scope: cardRef })

  return (
    <>
      <div ref={cardRef} className={`relative overflow-hidden group h-[42vh] min-h-[240px] sm:h-[50vh] md:h-[58vh] xl:h-screen`}>
      {/* h-[42vh] min-h-[240px] sm:h-[50vh] md:h-[58vh] lg:h-[72vh] */}
        <ImageComponent
          src={item?.image}
          alt={item?.label}
          object_cover={true}
          priority={true}
        />
        {/* Gradient — left-to-right fade keeps text legible at any vertical position */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.00) 100%)' }}
        />

        {/* Text starts at top of card, scrubs down to bottom as card scrolls through viewport */}
        <div
          ref={textRef}
          className="absolute top-0 left-0 p-5 md:p-10 text-white space-y-2 will-change-transform"
        >
          <p className="tracking-widest uppercase">{item?.label}</p>
          <h3 className="secondary_heading font-medium!">
            {item?.titleNormal}{' '}
            <span className="font-secondary italic font-medium!">{item?.titleItalic}</span>
          </h3>
          <Link
            href={`${getCategoriesPath()}?category=${encodeURIComponent(item?.href ?? '')}`}
            className="inline-flex items-center gap-2 text-sm"
          >
            Explore Shop <ArrowUpRightSVG />
          </Link>
        </div>
      </div>
    </>
  )
}

interface CategoryCardsProps {
  data: CategoryBanner[]
}

export default function CategoryCards({ data }: CategoryCardsProps) {
  const [first, second, ...rest] = data

  return (
    <>
      <div className="space-y-3 mx-3">
        {/* Top row — 2 large cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {first && <CategoryCard item={first} />}
          {second && <CategoryCard item={second} />}
        </div>

        {/* Bottom row — 3 equal cards */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {rest && rest?.length > 0 && rest?.map((item) => (
              <CategoryCard key={item?.id ?? ''} item={item} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
